<template>
  <div>
    <vue-element-loading :active="isLoading" />
    <v-card>
      <v-card-title>
        <v-icon color="primary"> mdi-credit-card-outline </v-icon>
        <span class="pl-2">{{ $t('pages.payments') }}</span>
        <v-spacer></v-spacer>
        <create-button @create="createPayment"></create-button>
      </v-card-title>
      <v-card-text class="pb-0">
        <v-row align="center">
          <v-col cols="12" sm="5" md="4">
            <v-text-field
              v-model="bankAccount"
              :label="$t('forms.iban')"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-bank-outline"
              :placeholder="$t('forms.iban_placeholder')"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="5" md="4">
            <v-text-field
              v-model="paymentDescription"
              :label="$t('forms.payment_instructions')"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-information-outline"
              :placeholder="$t('forms.payment_instructions_placeholder')"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-btn color="primary" @click="saveBankAccount" :loading="savingBank">
              <v-icon left>mdi-content-save</v-icon>
              {{ $t('actions.save') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <v-data-table
        :headers="headers"
        :items="payments"
        :options.sync="options"
        :server-items-length="totalPayments"
        class="elevation-1"
      >
        <template v-slot:top>
          <div class="mx-2 row">
            <div class="col-12">
              <v-text-field
                v-model="search"
                :label="$t('pages.search')"
              ></v-text-field>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-3">
              <v-autocomplete
                v-model="student_id"
                :items="students"
                item-text="name"
                item-value="id"
                clearable
                dense
                outlined
                :label="$t('forms.students')"
              ></v-autocomplete>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-3">
              <v-autocomplete
                v-model="created_by_id"
                :items="createdByUsers"
                item-text="name"
                item-value="id"
                clearable
                dense
                outlined
                :label="$t('forms.created_by')"
              ></v-autocomplete>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-3">
              <v-text-field
                v-model="amount"
                prefix="€"
                type="number"
                dense
                outlined
                :label="$t('forms.amount')"
                class="mx-2"
              ></v-text-field>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-3">
              <v-menu
                ref="monthsMenu"
                v-model="monthsMenu"
                :close-on-content-click="false"
                :return-value.sync="months"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-combobox
                    v-model="months"
                    multiple
                    dense
                    outlined
                    chips
                    small-chips
                    :label="$t('forms.months')"
                    append-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                  ></v-combobox>
                </template>
                <v-date-picker
                  v-model="months1"
                  type="month"
                  multiple
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="monthsMenu = false">
                    {{ $t('actions.cancel') }}
                  </v-btn>
                  <v-btn text color="primary" @click="saveMonths(months1)">
                    {{ $t('actions.ok') }}
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </div>
          </div>
        </template>

        <template v-slot:item.student_name="{ item }">
          <v-avatar rounded size="38">
            <v-img :src="getStudentAvatarUrl(item)"></v-img>
          </v-avatar>
          {{ item.student_name }}
        </template>

        <template v-slot:item.created_by_name="{ item }">
          <v-avatar rounded size="38">
            <v-img :src="getUserAvatarUrl(item)"></v-img>
          </v-avatar>
          {{ item.created_by_name }}
        </template>

        <template v-slot:item.amount="{ item }"> € {{ item.amount }} </template>

        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment('DD/MM/YYYY') }}</small> -
          <small class="text-muted">
            {{ item.created_at | moment('HH:mm') }}
          </small>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                small
                class="mr-2"
                @click="editPayment(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('actions.edit') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                small
                class="mr-2"
                @click="deletePayment(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('actions.delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading';
import { mesazhiIGabimit } from '@/utils/error-message';
import CreateButton from '@/components/CreateButton.vue';
import { Keys } from '@/config';

export default {
  components: { CreateButton, VueElementLoading },
  data() {
    return {
      isLoading: false,
      payments: [],
      search: '',
      headers: [
        { text: this.$t('table.student'), value: 'student_name' },
        { text: this.$t('table.created_by'), value: 'created_by_name' },
        { text: this.$t('table.amount'), value: 'amount' },
        { text: this.$t('table.months'), value: 'months' },
        { text: this.$t('table.created'), value: 'created_at' },
        { text: this.$t('table.actions'), value: 'actions', sortable: false },
      ],
      options: {},
      totalPayments: 0,
      student_id: null,
      students: [],
      created_by_id: null,
      createdByUsers: [],
      amount: null,
      months: [],
      months1: [],
      monthsMenu: null,
      bankAccount: null,
      paymentDescription: null,
      savingBank: false,
    };
  },
  mounted() {
    this.loadPayments();
    this.getAllStudents();
    this.getAllDriversList(true);
    this.loadBankAccount();
  },
  methods: {
    loadPayments() {
      this.isLoading = true;
      const params = {
        page: this.options?.page,
        per_page: this.options?.itemsPerPage,
        order_by: this.options?.sortBy?.[0],
        order_direction:
          this.options?.sortDesc?.[0] === null ||
          this.options?.sortDesc?.[0] === undefined
            ? null
            : this.options?.sortDesc?.[0] === true
            ? 'DESC'
            : 'ASC',
        search: this.search,
        student_id: this.student_id,
        creeated_by_id: this.created_by_id,
        amount: this.amount,
        months: this.months,
      };
      axios
        .get(`/school/payments`, { params })
        .then((response) => {
          this.payments = response?.data?.data;
          this.totalPayments = response?.data?.total;
        })
        .catch(() => {
          this.$notify({
            title: 'Error',
            text: 'Error while retrieving payments',
            type: 'error',
          });
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    getStudentAvatarUrl(user) {
      if (user?.student_avatar) {
        return (this.avatarImageUrl =
          Keys.VUE_APP_API_URL + user?.student_avatar);
      } else {
        return (this.avatarImageUrl =
          Keys.VUE_APP_API_URL + '/storage/avatars/1/avatar.png');
      }
    },
    getUserAvatarUrl(user) {
      if (user?.created_by_avatar) {
        return (this.avatarImageUrl =
          Keys.VUE_APP_API_URL + user?.created_by_avatar);
      } else {
        return (this.avatarImageUrl =
          Keys.VUE_APP_API_URL + '/storage/avatars/1/avatar.png');
      }
    },
    createPayment() {
      this.$router.push({
        name: 'create-school-payments',
      });
    },
    editPayment(payment) {
      this.$router.push({
        name: 'edit-school-payments',
        params: { payment_id: payment.id },
      });
    },
    deletePayment(payment) {
      this.$swal
        .fire({
          title: 'Delete Payment',
          text:
            "Are you sure to delete the payment of '" +
            payment.student_name +
            "' ?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, delete',
          confirmButtonColor: '#f44336',
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.deletePaymentServer(payment);
          }
        });
    },
    deletePaymentServer(payment) {
      this.isLoading = true;
      axios
        .delete(`/school/payments/${payment.id}`)
        .then(() => {
          this.$notify({
            title: 'Success',
            text: 'Payment deleted',
            type: 'success',
          });
          this.loadPayments();
        })
        .catch((error) => {
          this.$notify({
            title: 'Error',
            text: mesazhiIGabimit(error),
            type: 'error',
          });
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    loadBankAccount() {
      axios.get('/settings/bank-account')
        .then(r => { this.bankAccount = r.data.bank_account; this.paymentDescription = r.data.payment_description; })
        .catch(() => {});
    },
    saveBankAccount() {
      this.savingBank = true;
      axios.post('/settings/bank-account', { bank_account: this.bankAccount, payment_description: this.paymentDescription })
        .then(() => {
          this.$notify({ title: 'Sukses', text: 'Xhirollogaria u ruajt!', type: 'success' });
        })
        .catch(() => {
          this.$notify({ title: 'Gabim', text: 'Ndodhi nje gabim', type: 'error' });
        })
        .then(() => { this.savingBank = false; });
    },
    getAllStudents() {
      axios
        .get('/users/all-students-list')
        .then((response) => {
          this.students = response?.data;
        })
        .catch(() => {
          this.$notify({
            title: 'Error',
            text: mesazhiIGabimit(error),
            type: 'error',
          });
        });
    },
    getAllDriversList(request_user) {
      axios
        .get('/users/all-drivers-list', { params: { request_user } })
        .then((response) => {
          this.createdByUsers = response?.data;
        })
        .catch(() => {
          this.$notify({
            title: 'Error',
            text: 'Error fetching users',
            type: 'error',
          });
        });
    },
    saveMonths(months) {
      this.$refs.monthsMenu.save(months);
      this.months = months;
    },
  },
  watch: {
    options: {
      handler(val) {
        this.loadPayments();
      },
      deep: true,
    },
    search: {
      handler(val) {
        this.loadPayments();
      },
      deep: true,
    },
    search: function (val) {
      this.loadPayments();
    },
    student_id: function (val) {
      this.loadPayments();
    },
    created_by_id: function (val) {
      this.loadPayments();
    },
    amount: function (val) {
      this.loadPayments();
    },
    months: {
      handler(val) {
        this.loadPayments();
        this.months1 = this.months;
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped></style>
